/**
 * The firm's lines to the outside world — the SIMs in its gateway and its trunk — as the
 * administrator keeps them.
 *
 * A list with actions of its own, like the desk phones: saving a line is what writes its
 * endpoint to the PBX, so each one saves when it is pressed and the page's save bar does not
 * count them. The password goes in and never comes out; the list says when one was last
 * written, not what it was.
 */

import { request, type Narrow, type Schema } from '@taxi/api-client';

/** What a line physically is, which decides how many callers may wait on it. */
export type PhoneLineKind = 'Sim' | 'Trunk';

export const phoneLineKinds: readonly PhoneLineKind[] = ['Sim', 'Trunk'];

export type PhoneLine = Narrow<Schema<'PhoneLineView'>, { kind: PhoneLineKind }>;

export interface NewPhoneLine {
  name: string;
  label: string;
  number: string;
  kind: PhoneLineKind;
  address: string;
  password: string;
  dialPrefix: string;
}

/** What may change. The name is not among them: it is the account the gateway holds. */
export interface PhoneLineChange {
  label: string;
  number: string;
  kind: PhoneLineKind;
  address: string;

  /** A new one, or empty to keep the stored one. */
  password: string;
  dialPrefix: string;
}

const base = '/api/tenant/settings/telephony/lines';

export const linesApi = {
  list: () => request<PhoneLine[]>(base),

  add: (line: NewPhoneLine) => request<PhoneLine>(base, { method: 'POST', body: line }),

  update: (id: string, change: PhoneLineChange) =>
    request<PhoneLine>(`${base}/${id}`, { method: 'PUT', body: change }),

  remove: (id: string) => request<void>(`${base}/${id}`, { method: 'DELETE' }),
};
